import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  inject,
  input,
  output,
} from '@angular/core';

const TRAILING_PUNCTUATION = /[\s,.;:]+$/;

@Directive({
  selector: '[appTruncateText]',
  standalone: true,
})
export class TruncateTextDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private observer?: ResizeObserver;
  private frame?: number;

  readonly text = input.required<string>({ alias: 'appTruncateText' });
  readonly lines = input(2);
  readonly toggleLabel = input('See more');
  readonly truncatedText = output<string | null>();

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver(() => this.scheduleMeasure());
    this.observer.observe(this.element.nativeElement);
  }

  ngOnChanges(): void {
    this.scheduleMeasure();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
  }

  private scheduleMeasure(): void {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      const result = this.measure();
      if (result !== undefined) this.zone.run(() => this.truncatedText.emit(result));
    });
  }

  private measure(): string | null | undefined {
    const host = this.element.nativeElement;
    if (host.clientWidth === 0) return undefined;
    const style = getComputedStyle(host);
    const probe = document.createElement('div');
    Object.assign(probe.style, {
      position: 'absolute',
      visibility: 'hidden',
      left: '-9999px',
      top: '0',
      width: `${host.clientWidth}px`,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      fontStyle: style.fontStyle,
      letterSpacing: style.letterSpacing,
      lineHeight: style.lineHeight,
      whiteSpace: 'normal',
    });
    const label = document.createElement('span');
    label.textContent = this.toggleLabel();
    label.style.paddingLeft = 'var(--padding-4)';
    label.style.fontWeight = '500';
    document.body.appendChild(probe);
    probe.textContent = 'A';
    const maxHeight = probe.scrollHeight * this.lines() + 1;

    const fits = (content: string, withToggle: boolean): boolean => {
      probe.textContent = content;
      if (withToggle) probe.appendChild(label);
      return probe.scrollHeight <= maxHeight;
    };

    try {
      const text = this.text();
      if (fits(text, false)) return null;
      const words = text.split(/\s+/);
      let low = 0;
      let high = words.length - 1;
      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (fits(`${words.slice(0, middle).join(' ')}…`, true)) low = middle;
        else high = middle - 1;
      }
      return words.slice(0, low).join(' ').replace(TRAILING_PUNCTUATION, '');
    } finally {
      probe.remove();
    }
  }
}
